/**
 * Step 1 — Property Information
 *
 * Three discrete sections (Property Identity / Parking Permit Program /
 * Parking Lot Details), each in a white card. Inputs use the shared
 * .sales-input + .sales-label classes so spacing matches the rest of
 * the dispatch board (ExpenseForm, RequestForm, etc.).
 *
 * Brand accent: #3F66A0 (audit module color).
 *
 * State is owned by the parent wizard; this component is pure-controlled.
 */
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { api } from '../../../api';

const BRAND = '#3F66A0';
const BRAND_TINT = '#eef3fb';

const PARKING_PROGRAMS = [
  { value: 'myvip', label: 'MyVIP', sub: 'MyVIP digital permit system' },
  { value: 'permit', label: 'Permit', sub: 'Physical sticker / hangtag' },
];

const SPECIALTY_TAGS = ['Office', 'Leasing', 'EV Only', 'Maintenance', 'Fire Lane', 'Other'];

const cardStyle = {
  background: '#fff',
  borderRadius: 16,
  padding: '28px 32px',
  marginBottom: 20,
  border: '1px solid #e2e8f0',
};

const sectionTitleStyle = {
  margin: 0,
  marginBottom: 4,
  fontSize: 17,
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.01em',
};

const sectionHelpStyle = {
  marginTop: 0,
  marginBottom: 22,
  fontSize: 13,
  color: '#64748b',
  lineHeight: 1.5,
};

const requiredStar = <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>;

export default function Step1Property({ formData, onChange, onNext }) {
  const f = formData || {};
  const [propertySearch, setPropertySearch] = useState(f.property_name || '');
  const [propertyResults, setPropertyResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [otherMode, setOtherMode] = useState(!!f.property_label);
  const debounceRef = useRef(null);

  const totalSpaces = useMemo(() => (
    (parseInt(f.reserved || 0) || 0)
    + (parseInt(f.open_spaces || 0) || 0)
    + (parseInt(f.guest || 0) || 0)
    + (parseInt(f.handicap || 0) || 0)
    + (parseInt(f.specialty || 0) || 0)
  ), [f.reserved, f.open_spaces, f.guest, f.handicap, f.specialty]);

  const doSearch = useCallback((term) => {
    if (!term || term.length < 2) { setPropertyResults([]); return; }
    api.searchAuditProperties(term)
      .then(r => setPropertyResults(r.properties || []))
      .catch(() => setPropertyResults([]));
  }, []);

  const onSearchChange = (val) => {
    setPropertySearch(val);
    setShowResults(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const selectProperty = (p) => {
    onChange({
      ...f,
      property_id: p.uuid,
      property_name: p.name,
      property_address: p.address || '',
      property_label: null,
    });
    setPropertySearch(p.name);
    setShowResults(false);
    setOtherMode(false);
  };

  const switchToOther = () => {
    onChange({ ...f, property_id: null, property_name: '', property_label: '' });
    setPropertySearch('');
    setShowResults(false);
    setOtherMode(true);
  };

  const switchFromOther = () => {
    onChange({ ...f, property_label: null });
    setOtherMode(false);
  };

  const setField = (k, v) => onChange({ ...f, [k]: v });
  const toggleSpecialtyTag = (tag) => {
    const cur = new Set(f.specialty_tags || []);
    cur.has(tag) ? cur.delete(tag) : cur.add(tag);
    setField('specialty_tags', Array.from(cur));
  };

  const numField = (k, label) => (
    <div>
      <label className="sales-label">{label}</label>
      <input
        type="number"
        min="0"
        inputMode="numeric"
        className="sales-input"
        value={f[k] ?? 0}
        onChange={(e) => setField(k, e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value || 0)))}
      />
    </div>
  );

  const valid =
    (f.property_id || (f.property_label && f.property_label.trim()))
    && f.units > 0
    && f.parking_program;

  return (
    <div className="audit-step audit-step-1">

      {/* ─── Card 1: Property Identity ─── */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Property</h2>
        <p style={sectionHelpStyle}>
          Search the master property list. If the property isn't on file yet,
          type it in manually — a super-admin can link it to a real property record later.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          {/* Property Name */}
          <div>
            <label className="sales-label">Property Name {requiredStar}</label>
            {!otherMode ? (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="sales-input"
                  value={propertySearch}
                  placeholder="Start typing to search…"
                  onChange={(e) => onSearchChange(e.target.value)}
                  onFocus={() => setShowResults(true)}
                  onBlur={() => setTimeout(() => setShowResults(false), 200)}
                />
                {showResults && propertyResults.length > 0 && (
                  <ul style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    listStyle: 'none', padding: 0, margin: '4px 0 0',
                    background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
                    maxHeight: 280, overflowY: 'auto',
                  }}>
                    {propertyResults.map(p => (
                      <li
                        key={p.uuid}
                        onMouseDown={() => selectProperty(p)}
                        style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = BRAND_TINT}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{p.name}</div>
                        {p.address && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{p.address}</div>}
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  onClick={switchToOther}
                  style={{
                    marginTop: 8, padding: 0, background: 'none', border: 'none',
                    color: BRAND, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Property not in list? Type it manually →
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  className="sales-input"
                  value={f.property_label || ''}
                  placeholder="Type the property name"
                  onChange={(e) => setField('property_label', e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={switchFromOther}
                  style={{
                    marginTop: 8, padding: 0, background: 'none', border: 'none',
                    color: BRAND, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  ← Search master list instead
                </button>
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.5 }}>
                  This audit will be saved with the typed-in name. A super-admin can later
                  link it to the master property list.
                </p>
              </div>
            )}
          </div>

          {/* Number of Units */}
          <div>
            <label className="sales-label">Number of Units {requiredStar}</label>
            <input
              type="number"
              min="0"
              inputMode="numeric"
              className="sales-input"
              placeholder="0"
              value={f.units ?? ''}
              onChange={(e) => setField('units', parseInt(e.target.value || 0))}
            />
          </div>
        </div>
      </section>

      {/* ─── Card 2: Parking Permit Program ─── */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Parking Permit Program {requiredStar}</h2>
        <p style={sectionHelpStyle}>
          Which permit system does this property use to authorize parked vehicles?
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {PARKING_PROGRAMS.map(p => {
            const active = f.parking_program === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setField('parking_program', p.value)}
                style={{
                  padding: '18px 20px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                  border: active ? `2px solid ${BRAND}` : '2px solid #e2e8f0',
                  background: active ? BRAND_TINT : '#fff',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{
                  fontSize: 16, fontWeight: 700,
                  color: active ? BRAND : '#334155',
                }}>{p.label}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{p.sub}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ─── Card 3: Parking Lot Details ─── */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Parking Lot Details</h2>
        <p style={sectionHelpStyle}>
          Enter the count for each space type. Leave any non-applicable type at 0.
          The total updates automatically.
        </p>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16,
        }}>
          {numField('reserved', 'Total Reserved/Carport Spaces')}
          {numField('guest', 'Total Guest Spaces')}
          {numField('open_spaces', 'Total Open Parking Spaces')}
          {numField('handicap', 'Total Handicap Spaces')}
          {numField('specialty', 'Total Specialty Spaces')}
        </div>

        {/* Specialty type tags */}
        <div style={{ marginTop: 22 }}>
          <label className="sales-label">Specialty Type(s)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SPECIALTY_TAGS.map(tag => {
              const active = (f.specialty_tags || []).includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleSpecialtyTag(tag)}
                  style={{
                    padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                    border: active ? `2px solid ${BRAND}` : '2px solid #e2e8f0',
                    background: active ? BRAND : '#fff',
                    color: active ? '#fff' : '#475569',
                  }}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </div>

        {/* Total summary tile */}
        <div style={{
          marginTop: 24, padding: '20px 24px', borderRadius: 12,
          background: BRAND_TINT, border: `1px solid ${BRAND}33`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Total Parking Spaces
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Auto-calculated · live</div>
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: BRAND, lineHeight: 1 }}>
            {totalSpaces}
          </div>
        </div>
      </section>

      {/* Step actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button
          type="button"
          onClick={onNext}
          disabled={!valid}
          style={{
            padding: '14px 28px', borderRadius: 12, fontSize: 15, fontWeight: 700,
            border: 'none', cursor: valid ? 'pointer' : 'not-allowed',
            background: valid ? BRAND : '#cbd5e1',
            color: '#fff',
            transition: 'all 0.15s',
            boxShadow: valid ? '0 2px 6px rgba(63,102,160,0.25)' : 'none',
          }}
        >
          Next: Rent Roll →
        </button>
      </div>
    </div>
  );
}
